import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useRef, useState } from 'react'
import {
  Banner,
  Breadcrumbs,
  Button,
  LayerCard,
  ClipboardText,
  SkeletonLine,
  Collapsible,
  Text,
  Label,
  Grid,
  GridItem,
} from '@cloudflare/kumo'
import { WifiHigh, House } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'

interface NetworkInfo {
  name: string
  securityType: string
  vlanId: number
  password: string | null
  credential?: { username: string; password: string | null } | null
}

interface BrandingInfo {
  name: string
  domain: string
  accentColor: string | null
  welcomeCopy: string | null
}

interface NetworksResponse {
  guests: NetworkInfo[]
  coworkers: NetworkInfo[]
  branding: BrandingInfo | null
}

function NetworkCard({
  network,
  t,
  regenerating,
  onRegenerate,
}: {
  network: NetworkInfo
  t: (key: string) => string
  regenerating?: boolean
  onRegenerate?: () => void
}) {
  const hasEnterprise = network.securityType === 'WPA2/WPA3 Enterprise' && network.credential
  const hasPsk = network.securityType !== 'WPA2/WPA3 Enterprise' && network.password

  return (
    <LayerCard>
      <LayerCard.Secondary>
        <div>
          <div className="flex items-center gap-2">
            <WifiHigh size={16} className="text-kumo-subtle" />
            <Text as="span" variant="secondary">
              {network.securityType} &middot; VLAN {network.vlanId}
            </Text>
          </div>
        </div>
      </LayerCard.Secondary>
      <LayerCard.Primary>
        <div
          className="space-y-3"
          style={regenerating ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <div className="flex flex-col gap-1">
            <Label>{t('wifi.network')}</Label>
            <ClipboardText text={network.name} size="base" />
          </div>

          {hasEnterprise && (
            <>
              <div className="flex flex-col gap-1">
                <Label>{t('wifi.username')}</Label>
                <ClipboardText text={network.credential!.username} size="base" />
              </div>
              <div className="flex flex-col gap-1">
                <Label>{t('wifi.password')}</Label>
                <ClipboardText text={network.credential!.password ?? '\u2014'} size="base" />
              </div>
            </>
          )}

          {hasPsk && (
            <div className="flex flex-col gap-1">
              <Label>{t('wifi.password')}</Label>
              <ClipboardText text={network.password!} size="base" />
            </div>
          )}
        </div>
        {hasEnterprise && onRegenerate && (
          <div className="pt-3">
            <Button
              variant="secondary"
              size="sm"
              disabled={regenerating}
              loading={regenerating}
              onClick={onRegenerate}
            >
              {regenerating ? t('wifi.regenerating') : t('wifi.regeneratePassword')}
            </Button>
          </div>
        )}
      </LayerCard.Primary>
    </LayerCard>
  )
}

function SetupInstructions({
  networkName,
  username,
  password,
  t,
}: {
  networkName: string
  username: string
  password: string
  t: (key: string) => string
}) {
  const [openWindows, setOpenWindows] = useState(false)
  const [openMac, setOpenMac] = useState(false)
  const [openIos, setOpenIos] = useState(false)
  const [openAndroid, setOpenAndroid] = useState(false)

  return (
    <section className="flex flex-col gap-2 py-3 pl-3">
      <Text variant="heading2">{t('wifi.setup.title')}</Text>
      <Text variant="secondary">{t('wifi.setup.description')}</Text>
      <div className="space-y-2">
        <Collapsible label="Windows" open={openWindows} onOpenChange={setOpenWindows}>
          <div className="space-y-3 text-sm">
            <Text as="p">
              Select the WiFi network named <strong>'{networkName}'</strong> and enter your username
              and password listed above.
            </Text>
            <Text as="p">
              Then click <strong>'OK'</strong>.
            </Text>
            <Text as="p">
              After clicking 'OK' to connect your device to '{networkName}' for the first time, you
              will be prompted to trust the server certificate in order to connect. Trusting the
              certificate is required in order to connect to '{networkName}'. This is expected
              behavior when connecting to an 802.1X network.
            </Text>
            <Text as="p">You should be all set!</Text>
            <Text variant="secondary">
              Please note that if you forget the network on your device, you will be asked to trust
              the certificate again when you rejoin.
            </Text>
          </div>
        </Collapsible>

        <Collapsible label="macOS" open={openMac} onOpenChange={setOpenMac}>
          <div className="space-y-3 text-sm">
            <Text as="p">
              Select the WiFi network named <strong>'{networkName}'</strong> and fill in the form as
              follows:
            </Text>
            <ul className="list-none space-y-1 bg-kumo-recessed rounded-lg p-3 font-mono text-xs">
              <li>
                Mode: <strong>Automatic</strong>
              </li>
              <li>
                Username: <strong>'{username}'</strong>
              </li>
              <li>
                Password: <strong>'{password}'</strong>
              </li>
            </ul>
            <Text as="p">
              Then click <strong>'Join'</strong>.
            </Text>
            <Text as="p">
              After clicking 'Join' to connect your device to '{networkName}' for the first time,
              you will be prompted to trust the server certificate in order to connect. Trusting the
              certificate is required in order to connect to '{networkName}'. This is expected
              behavior when connecting to an 802.1X network.
            </Text>
            <Text as="p">You should be all set!</Text>
            <Text variant="secondary">
              Please note that if you forget the network on your device, you will be asked to trust
              the certificate again when you rejoin.
            </Text>
          </div>
        </Collapsible>

        <Collapsible label="iOS" open={openIos} onOpenChange={setOpenIos}>
          <div className="space-y-3 text-sm">
            <Text as="p">
              Select the WiFi network named <strong>'{networkName}'</strong> and enter your username
              and password listed above.
            </Text>
            <Text as="p">
              Then click <strong>'Join'</strong> and you should be all set!
            </Text>
          </div>
        </Collapsible>

        <Collapsible
          label="Android / Chromebook / Linux"
          open={openAndroid}
          onOpenChange={setOpenAndroid}
        >
          <div className="space-y-3 text-sm">
            <Text as="p">
              Select the WiFi network named <strong>'{networkName}'</strong>.
            </Text>
            <Text as="p">Adjust the following settings as needed for your particular device:</Text>
            <ul className="list-none space-y-1 bg-kumo-recessed rounded-lg p-3 font-mono text-xs">
              <li>
                Security: <strong>EAP</strong>
              </li>
              <li>
                EAP Method: <strong>PEAP</strong>
              </li>
              <li>
                EAP Phase 2 authentication: <strong>Automatic or MSCHAPV2</strong>
              </li>
              <li>
                Server CA certificate: <strong>Default or Use system certificates</strong>
              </li>
              <li>
                MAC Address Type: <strong>Phone MAC</strong>
              </li>
              <li>
                Domain: <strong>wenw.net</strong>
              </li>
              <li>
                Identity: <strong>'{username}'</strong>
              </li>
              <li>
                Password: <strong>'{password}'</strong>
              </li>
              <li>
                Anonymous identity: <strong>leave blank</strong>
              </li>
            </ul>
            <Text as="p">
              Select <strong>'Connect'</strong>.
            </Text>
            <Text as="p">Proceed with any certificate authentication prompts.</Text>
            <Text as="p">You should be all set!</Text>
          </div>
        </Collapsible>
      </div>
    </section>
  )
}

export function WifiCredentialsPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const [networks, setNetworks] = useState<NetworksResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const bannerTimer = useRef<ReturnType<typeof setTimeout>>()

  async function handleRegenerate() {
    if (!window.confirm(t('wifi.confirmRegenerate'))) return

    setRegenerating(true)
    setBanner(null)
    clearTimeout(bannerTimer.current)

    try {
      const token = await getAccessTokenSilently()
      const result = await apiRequest<{ password: string }>('/api/wifi/regenerate-password', {
        method: 'POST',
        token,
      })

      setNetworks((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          coworkers: prev.coworkers.map((n) =>
            n.securityType === 'WPA2/WPA3 Enterprise' && n.credential
              ? { ...n, credential: { ...n.credential, password: result.password } }
              : n,
          ),
        }
      })

      setBanner({ type: 'success', message: t('wifi.regenerateSuccess') })
    } catch {
      setBanner({ type: 'error', message: t('wifi.regenerateError') })
    } finally {
      setRegenerating(false)
      bannerTimer.current = setTimeout(() => setBanner(null), 3000)
    }
  }

  useEffect(() => {
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        await apiRequest('/api/auth/login-callback', { method: 'POST', token }).catch(() => {})
        const nets = await apiRequest<NetworksResponse>('/api/wifi/networks', {
          method: 'GET',
          token,
        })
        setNetworks(nets)
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError('NOT_ENROLLED')
        } else {
          setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
        }
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [getAccessTokenSilently])

  const accentStyle = networks?.branding?.accentColor
    ? ({ '--wifi-accent': networks.branding.accentColor } as React.CSSProperties)
    : undefined

  return (
    <div data-testid="wifi-page-root" style={accentStyle}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<House size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('wifi.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('wifi.title')}
        description={t('wifi.description')}
      />

      {!loading && !error && networks?.branding?.welcomeCopy && (
        <section data-testid="company-branded-block" className="flex flex-col gap-3 py-3 pl-3">
          <Text>{networks.branding.welcomeCopy}</Text>
        </section>
      )}

      {loading && (
        <>
          <section className="flex flex-col gap-2 py-3 pl-3">
            <SkeletonLine minWidth={30} maxWidth={40} className="h-5" />
            <Grid variant="2up" gap="base">
              {[1, 2].map((i) => (
                <GridItem key={i}>
                  <LayerCard>
                    <LayerCard.Primary>
                      <div className="space-y-3">
                        <div>
                          <SkeletonLine minWidth={15} maxWidth={20} className="h-3" />
                          <SkeletonLine minWidth={40} maxWidth={60} className="h-5" />
                        </div>
                        <div>
                          <SkeletonLine minWidth={15} maxWidth={20} className="h-3" />
                          <SkeletonLine minWidth={50} maxWidth={70} className="h-8" />
                        </div>
                        <div>
                          <SkeletonLine minWidth={15} maxWidth={20} className="h-3" />
                          <SkeletonLine minWidth={50} maxWidth={70} className="h-8" />
                        </div>
                      </div>
                    </LayerCard.Primary>
                  </LayerCard>
                </GridItem>
              ))}
            </Grid>
          </section>
        </>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')}{' '}
          {error === 'NOT_ENROLLED'
            ? t('common.notEnrolled')
            : error === 'GENERIC_ERROR'
              ? t('common.error')
              : error}
        </Banner>
      )}

      {!loading && !error && !networks && <Banner variant="error">{t('common.error')}</Banner>}

      {banner && (
        <div className="px-3 pt-3">
          <Banner variant={banner.type === 'success' ? 'default' : 'error'}>
            {banner.message}
          </Banner>
        </div>
      )}

      {!loading && !error && networks && networks.coworkers.length > 0 && (
        <>
          <section className="flex flex-col gap-2 py-3 pl-3">
            <Text variant="heading2">{t('wifi.coworkers.title')}</Text>
            <Grid variant="2up" gap="base">
              {networks.coworkers.map((n) => (
                <GridItem key={n.name}>
                  <NetworkCard
                    network={n}
                    t={t}
                    regenerating={
                      regenerating && n.securityType === 'WPA2/WPA3 Enterprise' ? true : undefined
                    }
                    onRegenerate={
                      n.securityType === 'WPA2/WPA3 Enterprise' && n.credential
                        ? handleRegenerate
                        : undefined
                    }
                  />
                </GridItem>
              ))}
            </Grid>
          </section>

          {(() => {
            const enterprise = networks.coworkers.find(
              (n) => n.securityType === 'WPA2/WPA3 Enterprise' && n.credential,
            )
            if (!enterprise?.credential) return null
            return (
              <>
                <SetupInstructions
                  networkName={enterprise.name}
                  username={enterprise.credential.username}
                  password={enterprise.credential.password ?? '\u2014'}
                  t={t}
                />
              </>
            )
          })()}
        </>
      )}
    </div>
  )
}
